import { useState, useEffect } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";

interface IntegrationRow {
  provider: string;
  status: string;
  last_sync_at: string | null;
  connected_at: string | null;
}

const knownIntegrations = [
  { provider: "ghl", label: "GoHighLevel CRM" },
  { provider: "meta_ads", label: "Meta Ads" },
  { provider: "google_ads", label: "Google Ads" },
];

export default function IntegrationsSettings() {
  const { clientId, role } = useRole();
  const [integrations, setIntegrations] = useState<Record<string, IntegrationRow>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIntegrations = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('integrations')
        .select('provider, status, last_sync_at, connected_at')
        .eq('client_id', clientId);

      if (!error && data) {
        const mapped: Record<string, IntegrationRow> = {};
        data.forEach((row: any) => {
          mapped[row.provider] = row;
        });
        setIntegrations(mapped);
      }
      setLoading(false);
    };

    fetchIntegrations();
  }, [clientId]);

  const isConnected = (provider: string) => {
    return integrations[provider]?.status === 'active';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="max-w-2xl">
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle>Connected Integrations</VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Connected Integrations</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent className="space-y-4">
          {!clientId && role === "admin" ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Integrations are managed per client. View a client's profile to manage their integrations.
            </p>
          ) : (
            <>
              {knownIntegrations.map((integration) => {
                const connected = isConnected(integration.provider);
                const row = integrations[integration.provider];
                const lastSync = row?.last_sync_at ? formatDate(row.last_sync_at) : null;
                const connectedAt = row?.connected_at ? formatDate(row.connected_at) : null;

                return (
                  <div
                    key={integration.provider}
                    className="flex items-center justify-between p-3 rounded-lg border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${connected ? 'bg-success' : 'bg-muted-foreground'}`} />
                      <div>
                        <p className="font-medium">{integration.label}</p>
                        {connected && lastSync && (
                          <p className="text-xs text-muted-foreground">Last sync: {lastSync}</p>
                        )}
                        {connected && !lastSync && connectedAt && (
                          <p className="text-xs text-muted-foreground">Connected: {connectedAt}</p>
                        )}
                        {!connected && (
                          <p className="text-xs text-muted-foreground">Not connected</p>
                        )}
                      </div>
                    </div>
                    {connected ? (
                      <BadgeVenture variant="success">
                        <Check className="w-3 h-3 mr-1" />
                        Connected
                      </BadgeVenture>
                    ) : (
                      <BadgeVenture variant="default">
                        <X className="w-3 h-3 mr-1" />
                        Not connected
                      </BadgeVenture>
                    )}
                  </div>
                );
              })}
            </>
          )}
          <p className="text-xs text-muted-foreground text-center pt-2">
            Integrations are managed by Venture Hills. Contact support for changes.
          </p>
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
